/**
 * Customization modal v2 — LIVE for every shopper since 2026-08-07. Layout v1
 * approved; v2/v3 stay reachable for comparison via ?modal=.
 *
 * TWO ENTRY POINTS, one modal core (`mountModal`):
 *   PDP  — the product page trigger; variant comes from the live picker (polled).
 *   CART — "View Customizations" on cart lines marked data-cust-format="v2";
 *          state is restored from the line's properties, the variant is fixed.
 * Legacy cart lines (data-cust-format="legacy") are NOT intercepted — the live
 * modal in product-customizations.js keeps handling them, untouched.
 *
 * Config source: shop metafield custom.customization_config (ONLY source).
 *
 * TODO before live launch (Vidas): replace full re-render with targeted DOM
 * updates (fixes focus loss in text fields).
 */
(function () {
  'use strict';

  // ---------- activation ----------
  // LAUNCHED 2026-08-07: the live-theme guard is gone, this modal now runs for
  // every shopper. `?modal=old` stays as the per-visitor fallback to the
  // previous modal — the fastest way to compare behaviour without a redeploy.
  //  ?modal=old      -> previous modal
  //  ?modal=v1|v2|v3 -> that layout
  //  no param        -> v1 (the approved layout)
  var params = new URLSearchParams(window.location.search);
  var p = params.get('modal');
  if (p === 'old') return;
  var LAYOUT = ['v1', 'v2', 'v3'].indexOf(p) !== -1 ? p : 'v1';

  function usableConfig(c) {
    return c && c.pricing && c.pricing.by_currency && c.pricing.tiers &&
      c.categories && c.ui && c.ui.step3 ? c : null;
  }
  var CFG = usableConfig(window.NPL_CUSTOMIZATION_CONFIG_METAFIELD);
  if (!CFG) { console.error('[modal-v2] config metafield missing or invalid'); return; }
  console.log('[modal-v2] config source: metafield (version ' + CFG.version + ')');

  // ---------- shared helpers ----------
  function resolveCategory(tag) {
    var c = CFG.categories[tag];
    if (c && c.placeholder_from) c = CFG.categories[c.placeholder_from];
    return c || CFG.categories['cust-dresses'];
  }
  function categoryTagOf(productTags) {
    return productTags.find(function (t) { return /^cust-(dresses|pants|skirts|tops)$/.test(t); });
  }
  // Property NAME essence — never the full UI sentence, never a blocked template.
  function shortLabel(it) {
    if (it.label_short) return it.label_short;
    return String(it.text || '').split(' (')[0].replace(/\.$/, '');
  }
  // Step 1 dropdowns. Tag logic identical to the live modal; pocket options are
  // v2-only (removal free here, style change is the paid Step 3 item).
  // Every dropdown opens on the same word: "Standard", matching the live modal.
  // It is both the option label and what the Step 1 summary reports when the
  // shopper leaves a dropdown alone.
  var CHOICE_PLACEHOLDER = 'Standard';
  function choicesFor(productTags) {
    return [
      { tag: 'custom-closures', label: 'Closures', placeholder: CHOICE_PLACEHOLDER,
        options: ['Buttons', 'Snaps'] },
      { tag: 'custom-waist-ties', label: 'Waist Ties', placeholder: CHOICE_PLACEHOLDER,
        options: productTags.indexOf('custom-waist-no-tie') !== -1
          ? ['Removable waist ties (2 back loops)', 'Sewn-in side waist ties (0.98"/2.5 cm)', 'No Ties']
          : ['Removable waist ties (2 back loops)', 'Sewn-in side waist ties (0.98"/2.5 cm)'] },
      { tag: 'custom-pockets', label: 'Pockets', placeholder: CHOICE_PLACEHOLDER,
        options: ['No Pockets (Remove Pockets)'] }
    ].filter(function (c) { return productTags.indexOf(c.tag) !== -1; });
  }

  // Property names the modal writes itself besides the per-item ones.
  var FEE_PROPERTY_NAMES = ['Customizations fee', '_fee_breakdown', '_fee_total'];

  /**
   * Restore modal state from a cart line's properties.
   * `consumed` lists every property name the modal understood; the cart edit
   * carries every other property over unchanged (row 1187).
   * Name shape: "<label> (free)" | "<label> (<amount> <CUR>)". The greedy label
   * capture keeps labels that contain their own parentheses intact
   * ("Add a separate linen belt (200 cm) (14 EUR)").
   * KEYS are authoritative from _fee_breakdown; label matching only supplies texts.
   */
  function stateFromProperties(props, category, choiceLabels) {
    var state = {
      choices: {}, step2: null, step2Text: '', step2Saved: false,
      requests: { standard: [], advanced: [] }, triedSave: false,
    };
    var notes = [];
    var consumed = FEE_PROPERTY_NAMES.slice();
    var byLabelFree = {};
    (category.step2 || []).forEach(function (it) { byLabelFree[shortLabel(it)] = it; });

    // Step-3 requests come back as "Customization N (9 EUR)". N gives the order,
    // and _fee_breakdown gives the tier per position — the two were written in
    // the same order, so index alignment is exact.
    var numbered = [];
    Object.keys(props || {}).forEach(function (name) {
      if (name.charAt(0) === '_') return;
      var m = /^(.*) \(([^()]*)\)$/.exec(name);
      if (!m) return; // legacy or unrelated property
      var label = m[1], marker = m[2], value = props[name];
      if (marker === 'free') {
        if (choiceLabels.indexOf(label) !== -1) { state.choices[label] = value; consumed.push(name); return; }
        var free = byLabelFree[label];
        // Already in the cart == already confirmed.
        if (free) { state.step2 = free.key; state.step2Text = value; state.step2Saved = true; consumed.push(name); }
        else notes.push(name);
        return;
      }
      var num = /^Customization (\d+)$/.exec(label);
      if (num) { numbered.push({ n: Number(num[1]), text: value }); consumed.push(name); }
      else notes.push(name);
    });
    numbered.sort(function (a, b) { return a.n - b.n; });

    var breakdown = [];
    try { breakdown = JSON.parse(props._fee_breakdown || '[]') || []; } catch (e) { breakdown = []; }
    var std = CFG.request_keys.standard;
    numbered.forEach(function (entry, i) {
      var b = breakdown[i];
      var tier = b && b.item && b.item !== std ? 'advanced' : 'standard';
      // Anything already in the cart was confirmed when it was added.
      state.requests[tier].push({ text: entry.text, saved: true, everSaved: true });
    });
    if (breakdown.length !== numbered.length) {
      notes.push('breakdown/property count mismatch: ' + breakdown.length + ' vs ' + numbered.length);
    }

    return { state: state, notes: notes, consumed: consumed, storedTotal: props._fee_total || '' };
  }

  /**
   * The modal itself. ctx:
   *   mode          'pdp' | 'cart'
   *   productTags   string[]
   *   currency      ISO code
   *   moneyFormat   theme money format
   *   productHandle for /products/<handle>.js (pdp)
   *   variants      cart mode: product.variants
   *   variantId     cart mode: the line's variant
   *   quantity      cart mode: the line's quantity (preserved on save)
   *   cartItemKey   cart mode
   *   initial       cart mode: { state, notes, storedTotal }
   */
  function mountModal(ctx) {
    var productTags = ctx.productTags || [];
    var currency = ctx.currency || 'USD';
    var moneyFormat = ctx.moneyFormat || '${{amount}}';
    var custTag = categoryTagOf(productTags);
    var category = resolveCategory(custTag || 'cust-dresses');
    // Step 3 is a COUNT of requests per tier, not a set of ticked items: the
    // item lists are informational and each "Add Customization" block bills one
    // tier price. That is what makes "each change is a separate customization"
    // true in the money, not just in the copy.
    var state = (ctx.initial && ctx.initial.state) ||
      { choices: {}, step2: null, step2Text: '', step2Saved: false,
        requests: { standard: [], advanced: [] }, triedSave: false };

    // ---------- pricing ----------
    function tierPrice(tier) {
      var row = CFG.pricing.by_currency[currency];
      if (row) return row[tier];
      var usd = CFG.pricing.tiers[tier];
      var rate = window.Shopify && window.Shopify.currency && window.Shopify.currency.rate
        ? parseFloat(window.Shopify.currency.rate) : 0;
      if (rate > 0) return Math.round(usd * rate); // per-tier, Phase-1 mechanism
      return usd;
    }
    function fmt(units) {
      var cents = Math.round(units * 100);
      if (window.theme && window.theme.formatMoney) {
        return window.theme.formatMoney(cents, moneyFormat).replace(/<[^>]*>/g, '');
      }
      return units.toFixed(2) + ' ' + currency;
    }
    function itemsByKey() {
      var map = {};
      ['step2', 'step3_standard', 'step3_advanced'].forEach(function (l) {
        (category[l] || []).forEach(function (it) { map[it.key] = it; });
      });
      return map;
    }
    // A block starts counting when it is first saved, and keeps counting while
    // it is being edited — the request still exists, so dropping the price
    // mid-edit would only alarm. Only "× Remove" takes it back out.
    // Safety net: invalidKeys() refuses the bottom Save while any block is in
    // the unsaved state, and buildProperties() writes saved blocks only. So a
    // shopper can never leave the modal with a counted-but-unwritten block.
    function countedRequests(tier) {
      return state.requests[tier].filter(function (e) { return e.saved || e.everSaved; });
    }
    function savedRequests(tier) {
      return state.requests[tier].filter(function (e) { return e.saved; });
    }
    function requestCount() {
      return countedRequests('standard').length + countedRequests('advanced').length;
    }
    function total() {
      return countedRequests('standard').length * tierPrice('standard') +
        countedRequests('advanced').length * tierPrice('advanced');
    }
    function anySelection() {
      return !!state.step2 || requestCount() > 0 ||
        Object.keys(state.choices).some(function (k) { return state.choices[k]; });
    }
    function invalidKeys() {
      var bad = [];
      // Step 2 follows the same rule as Step 3: unsaved blocks the bottom Save.
      // It is free, but the behaviour has to be identical everywhere.
      if (state.step2 && !state.step2Saved) bad.push('step2');
      // Unsaved blocks block the bottom Save, not just empty ones. Without this
      // a shopper could type, hit Edit, forget to save, and lose the text at
      // checkout — the totals no longer count it and neither does the cart.
      ['standard', 'advanced'].forEach(function (tier) {
        state.requests[tier].forEach(function (e, i) { if (!e.saved) bad.push(tier + ':' + i); });
      });
      return bad;
    }

    // ---------- lengthen rules ----------
    var noLengthenTag = productTags.indexOf('no-lengthen') !== -1;
    var productVariants = ctx.variants || null;
    var maxVariantPrice = 0, maxVariantSizeValue = '';
    var currentHandle = ctx.productHandle || '';
    function ingestProduct(pj) {
      productVariants = pj.variants || [];
      computeVariantExtremes(pj);
    }
    function computeVariantExtremes(pj) {
      maxVariantPrice = (productVariants || []).reduce(function (mx, v) { return Math.max(mx, v.price); }, 0);
      var maxV = (productVariants || []).find(function (v) { return v.price === maxVariantPrice; });
      if (maxV) {
        var opts = (pj && pj.options) || [];
        var sizeIdx = opts.findIndex(function (o) {
          return /size/i.test(typeof o === 'string' ? o : (o && o.name) || '');
        });
        maxVariantSizeValue = (sizeIdx >= 0 && maxV.options && maxV.options[sizeIdx]) || maxV.title || '';
      }
    }
    function selectedVariant() {
      if (!productVariants) return null;
      if (ctx.mode === 'cart') {
        return productVariants.find(function (v) { return Number(v.id) === Number(ctx.variantId); }) || null;
      }
      var input = document.querySelector('form[data-product-form] [name="id"]') ||
        document.querySelector('form[action*="/cart/add"] [name="id"]');
      if (!input) return null;
      return productVariants.find(function (v) { return Number(v.id) === Number(input.value); }) || null;
    }
    function priceBlocked() {
      if (!productVariants || !maxVariantPrice) return false;
      var v = selectedVariant();
      return !!v && Number(v.price) < maxVariantPrice;
    }
    function shortenOnly() { return noLengthenTag || priceBlocked(); }
    function effectiveItemText(it) {
      if (!it.lengthen || !shortenOnly()) return it.text;
      if (noLengthenTag && it.blocked_text_product) {
        var sibling = ((CFG.ui.no_lengthen || {}).sibling_by_handle || {})[currentHandle] || 'the longer version';
        return it.blocked_text_product.replace('{product}', sibling);
      }
      if (it.blocked_text_variant) {
        return it.blocked_text_variant.replace('{variant}', lengthPartOf(maxVariantSizeValue) || 'the longer length');
      }
      return it.text;
    }
    // "XS - Long MAXI (49.2 in | 125 cm)" -> "Long MAXI": the blocked text names
    // only the LENGTH part; a size prefix would mislead a different-size shopper.
    function lengthPartOf(sizeValue) {
      return String(sizeValue || '')
        .replace(/^\s*(XXS|XS|S\/M|M\/L|XXL|XL|S|M|L)\s*-\s*/i, '')
        .replace(/\s*\([^)]*\)\s*$/, '')
        .trim();
    }
    var lastBlocked = null;
    function syncVariantRule() {
      var nowBlocked = priceBlocked();
      var transitioned = lastBlocked === false && nowBlocked === true;
      var changed = lastBlocked !== null && lastBlocked !== nowBlocked;
      lastBlocked = nowBlocked;
      if (dialog && dialog.open) updateContextLine();
      var removed = false;
      if (transitioned && !noLengthenTag) {
        // Only Step 2 carries an item identity now; Step 3 requests are free
        // text, so there is nothing there to invalidate — the informational
        // list re-renders with the blocked wording instead.
        var map = itemsByKey();
        if (state.step2 && (map[state.step2] || {}).lengthen) { state.step2 = null; state.step2Text = ''; removed = true; }
        if (removed) notice(CFG.ui.no_lengthen.removed_msg);
      }
      if (dialog && dialog.open && (changed || removed)) render();
      // Row 1187: the rule used to run only while the modal was open, so a
      // lengthen request saved on the longest variant still shipped after the
      // shopper switched to a shorter one. The removal now also reaches the
      // COMMITTED state and the product form at once — open or closed — so
      // neither a Discard nor a later Add to cart can bring the item back.
      // On every transition, not only when the live state lost the item: the
      // shopper may have switched Step 2 away in the open modal while the
      // committed state still holds the lengthen item.
      if (transitioned && !noLengthenTag && ctx.mode === 'pdp' && dialog) dropLengthenFromCommitted();
      return removed;
    }
    function dropLengthenFromCommitted() {
      var c = JSON.parse(committed);
      var item = c.step2 && itemsByKey()[c.step2];
      if (!item || !item.lengthen) return;
      c.step2 = null; c.step2Text = ''; c.step2Saved = false;
      var live = snapshot();
      var liveTried = state.triedSave;
      restore(JSON.stringify(c));
      if (writePdpProperties()) updatePdpSurface();
      // restore() swaps in fresh objects; re-render so the open body's handlers
      // point at the live state again.
      if (dialog.open) { restore(live); state.triedSave = liveTried; render(); return; }
      var status = ctx.host && ctx.host.querySelector('[data-status-message]');
      if (status) {
        status.innerHTML = '<strong>' + CFG.ui.no_lengthen.removed_msg + '</strong>';
        status.style.display = 'block';
      }
    }
    function notice(msg) {
      var n = dialog.querySelector('[data-v2-notice]');
      if (!n) return;
      n.textContent = msg;
      n.style.display = 'block';
    }

    // ---------- dom helpers ----------
    function el(tag, style, html) {
      var e = document.createElement(tag);
      if (style) e.style.cssText = style;
      if (html !== undefined) e.innerHTML = html;
      return e;
    }
    var S = {
      h: 'font-weight:500;font-size:1rem;color:#000;margin:0 0 8px;',
      // Intro paragraphs match the live modal's intro: 0.9rem / #666 / lh 1.5.
      // (0.85rem here made Step 2 and Step 3 read smaller than Step 1.)
      p: 'font-size:0.9rem;color:#666;line-height:1.5;margin:0 0 16px;text-align:left;',
      box: 'padding:14px 0;',
      ta: 'width:100%;margin-top:8px;padding:10px;border:1px solid #ddd;border-radius:4px;font-size:16px;font-family:inherit;box-sizing:border-box;',
      price: 'margin-left:auto;font-weight:600;font-size:0.9rem;color:#000;white-space:nowrap;',
      // Lifted verbatim from the live modal so a saved block looks identical.
      savedBadge: 'color:#059669;font-size:0.75rem;font-weight:600;margin-left:8px;',
      savedBox: 'background:#f9f9f9;padding:12px;border:none;border-radius:4px;color:#333;' +
        'font-size:0.9rem;line-height:1.4;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;' +
        'cursor:default;min-height:44px;display:flex;align-items:center;margin-top:8px;',
      linkBtn: 'background:none;border:none;color:#666;cursor:pointer;padding:4px 8px;' +
        'font-size:0.8rem;text-decoration:underline;',
      saveBtn: 'background:#000;color:white;border:none;padding:6px 16px;border-radius:4px;' +
        'cursor:pointer;font-size:0.85rem;font-weight:500;transition:background 0.2s;'
    };
    function buildTextField(value, invalid, oninput, item, focusKey) {
      var shortenMode = item && item.lengthen && shortenOnly();
      var wrap = el('div', 'width:100%;');
      var taId = uid('ta');
      var label = el('label', '',
        shortenMode ? CFG.ui.no_lengthen.shorten_field_label : "Please describe exactly what you'd like");
      label.className = 'v2-ta-label';
      label.htmlFor = taId;
      var ta = document.createElement('textarea');
      ta.id = taId;
      if (focusKey) ta.setAttribute('data-focus-key', focusKey);
      ta.rows = 3;
      ta.style.cssText = S.ta;
      if (invalid) ta.className = 'v2-ta-err';
      ta.value = value;
      ta.addEventListener('input', function () { oninput(ta.value); refreshFooter(); });
      wrap.appendChild(label); wrap.appendChild(ta);
      return wrap;
    }
    function holderStyle(selected) {
      return 'width:100%;padding:6px 8px;margin:2px 0;border-left:2px solid ' +
        (selected ? '#000' : 'transparent') + ';border-radius:0 4px 4px 0;' +
        (selected ? 'background:#FBFAF5;' : '');
    }

    // ---------- steps ----------
    function buildStep1(withHeading) {
      var wrap = el('div', S.box);
      if (withHeading !== false) wrap.appendChild(el('h4', S.h, CFG.ui.step1.heading));
      wrap.appendChild(el('p', 'font-size:0.9rem;color:#666;line-height:1.5;margin:0 0 24px;text-align:left;', CFG.ui.step1.text));
      var allChoices = choicesFor(productTags);
      if (allChoices.length === 0) {
        wrap.appendChild(el('p', S.p + 'font-style:italic;', CFG.ui.step1.no_options_sentence));
        return wrap;
      }
      allChoices.forEach(function (c) {
        var row = el('div', 'display:flex;align-items:center;gap:8px;margin:0 0 16px;padding:0;');
        var selId = uid('s1');
        var lab = el('label', 'min-width:70px;flex-shrink:0;font-size:0.9rem;font-weight:500;margin:0;padding:0;', c.label);
        lab.htmlFor = selId;
        row.appendChild(lab);
        var sel = document.createElement('select');
        sel.id = selId;
        sel.setAttribute('data-focus-key', 's1:' + c.label);
        // flex-basis 0 + min-width 0: the select shrinks below its longest option,
        // so the "✓ Selected" badge stays inside the card on a phone (row 1187).
        sel.style.cssText = 'flex:1 1 0;min-width:0;padding:10px;border:1px solid #ddd;border-radius:4px;font-size:0.95rem;background:white;color:#000;';
        sel.innerHTML = '<option value="">' + (c.placeholder || CHOICE_PLACEHOLDER) + '</option>' +
          c.options.map(function (o) {
            return '<option' + (state.choices[c.label] === o ? ' selected' : '') + '>' + o + '</option>';
          }).join('');
        sel.addEventListener('change', function () { state.choices[c.label] = sel.value; refresh(); });
        row.appendChild(sel);
        // Step 1 needs no Save: the dropdown IS the confirmation. The badge is
        // ALWAYS shown — "Standard" is a valid choice (she gets what the photos
        // show), so the tick confirms the value is set, not that it was changed.
        // Same rule as the section tick in the collapsed header.
        row.appendChild(el('span', S.savedBadge + 'white-space:nowrap;', '✓ Selected'));
        wrap.appendChild(row);
      });
      return wrap;
    }

    function buildStep2(withHeading) {
      var wrap = el('div', S.box);
      if (withHeading !== false) wrap.appendChild(el('h4', S.h, CFG.ui.step2.heading));
      wrap.appendChild(el('p', S.p, CFG.ui.step2.text));
      (category.step2 || []).forEach(function (it) {
        var selected = state.step2 === it.key;
        var holder = el('div', holderStyle(selected));
        var row = el('div', 'display:flex;align-items:flex-start;gap:8px;');
        var radio = document.createElement('input');
        radio.type = 'radio'; radio.name = 'npl-step2'; radio.checked = selected;
        radio.setAttribute('data-focus-key', 's2:' + it.key);
        radio.style.cssText = 'margin-top:3px;';
        radio.addEventListener('click', function () {
          if (state.step2 === it.key) { radio.checked = false; state.step2 = null; state.step2Text = ''; }
          else { state.step2 = it.key; state.step2Text = ''; }
          state.step2Saved = false;
          refresh();
        });
        var label = el('label', 'font-size:0.9rem;flex:1;cursor:pointer;margin:0;', effectiveItemText(it));
        label.addEventListener('click', function () { radio.click(); });
        row.appendChild(radio); row.appendChild(label);
        if (selected && state.step2Saved) {
          row.appendChild(el('span', S.savedBadge, '✓ Saved'));
          var s2ed = el('button', '', 'Edit');
          s2ed.className = 'v2-linkbtn';
          s2ed.type = 'button';
          s2ed.setAttribute('data-focus-key', 's2:edit');
          s2ed.addEventListener('click', function () { state.step2Saved = false; refresh(); });
          row.appendChild(s2ed);
        }
        holder.appendChild(row);
        // Step 2 confirms the same way Step 3 does — it is the same kind of
        // required free text, and in the live modal the free customization was
        // just customization #1 and carried a Save button too.
        if (selected && state.step2Saved) {
          var s2box = el('div', S.savedBox);
          s2box.textContent = state.step2Text;
          holder.appendChild(s2box);
        } else if (selected) {
          holder.appendChild(buildTextField(state.step2Text,
            state.triedSave && !state.step2Saved,
            function (v) { state.step2Text = v; }, it, 's2:ta'));
          var s2row = el('div', 'display:flex;justify-content:flex-end;align-items:center;margin-top:8px;');
          var s2sv = el('button', S.saveBtn, 'Save Customization');
          s2sv.type = 'button';
          s2sv.setAttribute('data-focus-key', 's2:save');
          s2sv.addEventListener('mouseover', function () { s2sv.style.background = '#333'; });
          s2sv.addEventListener('mouseout', function () { s2sv.style.background = '#000'; });
          s2sv.addEventListener('click', function () {
            if (!state.step2Text.trim()) { state.triedSave = true; } else { state.step2Saved = true; }
            refresh();
          });
          s2row.appendChild(s2sv);
          holder.appendChild(s2row);
        }
        wrap.appendChild(holder);
      });
      // Footer sits after ALL the radio items — it explains what happens when
      // one free adjustment is not enough, which only lands once the shopper
      // has seen the list.
      if (CFG.ui.step2.footer_text) {
        // Helper copy matches the live modal's helper size: 0.85rem / #666.
        wrap.appendChild(el('p', 'font-size:0.85rem;color:#666;line-height:1.5;margin:14px 0 0;text-align:left;',
          CFG.ui.step2.footer_text));
      }
      return wrap;
    }

    function tierHeading(tier) {
      var text = tier === 'standard' ? CFG.ui.step3.standard_heading : CFG.ui.step3.advanced_heading;
      return text.replace(/1[05] eur/, fmt(tierPrice(tier)));
    }

    // Step 2 hands one adjustment over for free, and the Step 3 Standard list
    // names the same adjustments as paid ones. The two sides of a pair are the
    // same key under a different prefix (s2_x / std_x), so once the shopper has
    // taken hers for free, the paid twin is dropped from the informational list
    // below — leaving it there reads as the same change being sold twice.
    //
    // Pairing is by KEY, never by text: the wording differs per category and
    // gets rewritten, the keys are the launch contract. Deriving the twin
    // instead of hard-coding five pairs is what makes pants/skirts/tops correct
    // for free. s2_remove_details has no std_ twin, so it hides nothing, and
    // Advanced is never touched.
    function step2TwinKey() {
      if (!state.step2 || state.step2.indexOf('s2_') !== 0) return null;
      return 'std_' + state.step2.slice(3);
    }

    function buildTier(listName, withHeading) {
      var tier = listName === 'step3_standard' ? 'standard' : 'advanced';
      var priceUnits = tierPrice(tier);
      var wrap = el('div', S.box);
      if (withHeading) wrap.appendChild(el('h4', S.h, tierHeading(tier)));
      if (tier === 'advanced') wrap.appendChild(el('p', S.p, CFG.ui.step3.advanced_intro));

      // INFORMATIONAL list — no inputs. It tells the shopper what belongs in
      // this tier; the request blocks below are what actually gets ordered.
      var hiddenTwin = listName === 'step3_standard' ? step2TwinKey() : null;
      var list = el('ul', 'margin:0 0 14px;padding:0 0 0 18px;list-style:disc;');
      (category[listName] || []).forEach(function (it) {
        if (it.billing_only) return;
        if (it.key === hiddenTwin) return;
        var li = el('li', 'font-size:0.9rem;color:#000;line-height:1.5;margin:0 0 4px;', effectiveItemText(it));
        list.appendChild(li);
      });
      wrap.appendChild(list);
      if (CFG.ui.step3.add_hint) {
        wrap.appendChild(el('p', 'font-size:0.85rem;color:#666;line-height:1.5;margin:0 0 14px;text-align:left;',
          CFG.ui.step3.add_hint));
      }

      // One block per requested change. No cap, and — unlike the live modal —
      // no requirement to save one before adding the next: Save here is a
      // confirmation, not a lock.
      state.requests[tier].forEach(function (entry, idx) {
        var holder = el('div', holderStyle(true));
        var head = el('div', 'display:flex;align-items:center;gap:8px;');
        head.appendChild(el('span', 'font-size:0.9rem;font-weight:500;color:#000;',
          'Customization ' + (idx + 1)));
        if (entry.saved) head.appendChild(el('span', S.savedBadge, '✓ Saved'));
        head.appendChild(el('span', S.price, fmt(priceUnits)));
        if (entry.saved) {
          var ed = el('button', '', 'Edit');
          ed.className = 'v2-linkbtn';
          ed.type = 'button';
          ed.setAttribute('data-focus-key', tier + ':' + idx + ':edit');
          ed.addEventListener('click', function () { entry.saved = false; refresh(); });
          head.appendChild(ed);
        }
        var rm = el('button', 'color:#999;', '&times; Remove');
        rm.className = 'v2-linkbtn';
        rm.type = 'button';
        rm.setAttribute('data-focus-key', tier + ':' + idx + ':rm');
        rm.addEventListener('click', function () {
          state.requests[tier].splice(idx, 1);
          refresh();
        });
        head.appendChild(rm);
        holder.appendChild(head);

        if (entry.saved) {
          // Read-only view, styled like the live modal's saved customization.
          var box = el('div', S.savedBox);
          box.textContent = entry.text;
          holder.appendChild(box);
        } else {
          holder.appendChild(buildTextField(entry.text,
            state.triedSave && !entry.saved,
            function (v) { entry.text = v; }, null, tier + ':' + idx + ':ta'));
          var row = el('div', 'display:flex;justify-content:flex-end;align-items:center;margin-top:8px;');
          var sv = el('button', S.saveBtn, 'Save Customization');
          sv.type = 'button';
          sv.setAttribute('data-focus-key', tier + ':' + idx + ':save');
          sv.addEventListener('mouseover', function () { sv.style.background = '#333'; });
          sv.addEventListener('mouseout', function () { sv.style.background = '#000'; });
          sv.addEventListener('click', function () {
            if (!entry.text.trim()) { state.triedSave = true; } else { entry.saved = true; entry.everSaved = true; }
            refresh();
          });
          row.appendChild(sv);
          holder.appendChild(row);
        }
        wrap.appendChild(holder);
      });

      // Button on the left, this tier's running total on the right. Each tier
      // shows only its OWN total — the combined figure lives in the footer.
      var addRow = el('div', 'display:flex;align-items:center;justify-content:space-between;gap:12px;margin-top:6px;');
      var add = el('button',
        'display:inline-block;padding:8px 14px;border:1px dashed #999;background:#fff;' +
        'border-radius:4px;cursor:pointer;font-size:0.85rem;color:#000;', '+ Add Customization');
      add.type = 'button';
      add.setAttribute('data-focus-key', 'add:' + tier);
      add.addEventListener('click', function () {
        state.requests[tier].push({ text: '', saved: false, everSaved: false });
        refresh();
      });
      addRow.appendChild(add);
      var savedHere = countedRequests(tier).length;
      if (savedHere > 0) {
        addRow.appendChild(el('span', 'font-weight:600;font-size:0.9rem;color:#000;white-space:nowrap;',
          'Total: ' + fmt(savedHere * priceUnits)));
      }
      wrap.appendChild(addRow);
      return wrap;
    }

    function buildStep3(tiersCollapsed, withHeading) {
      var wrap = el('div', S.box);
      if (withHeading !== false) wrap.appendChild(el('h4', S.h, CFG.ui.step3.heading));
      wrap.appendChild(el('p', S.p, CFG.ui.step3.intro));
      if (tiersCollapsed) {
        wrap.appendChild(collapsible('std', tierHeading('standard'), buildTier('step3_standard', false), false, metaTier('step3_standard')));
        wrap.appendChild(collapsible('adv', tierHeading('advanced'), buildTier('step3_advanced', false), false, metaTier('step3_advanced')));
      } else {
        wrap.appendChild(buildTier('step3_standard', true));
        wrap.appendChild(buildTier('step3_advanced', true));
      }
      return wrap;
    }

    function buildStep4() {
      var body = el('div', 'padding:6px 0 14px;');
      body.appendChild(el('p', S.p, CFG.ui.step4.intro));
      // White type on the gold, matching the heading bar. It measures 2.9:1
      // against #A99843 — Vidas's call on 2026-08-10, made against the numbers
      // and a screenshot of each option; see the IPS block in the stylesheet.
      var a = el('a', 'display:inline-block;padding:10px 16px;border:1px solid #A99843;border-radius:4px;' +
        'background:#A99843;color:#fff;text-decoration:none;font-size:0.9rem;', 'Contact us');
      var title = ctx.productTitle || (document.querySelector('h1') ? document.querySelector('h1').textContent : document.title);
      a.href = '/pages/individual-pattern-service?product=' + encodeURIComponent(String(title).trim());
      body.appendChild(a);
      var d = collapsible('ips', CFG.ui.step4.heading, body, false, {});
      d.className = 'v2-acc v2-acc--ips';
      return d;
    }

    // ---------- collapsible sections with collapsed-state summaries ----------
    var openMap = {};
    // Opening a step folds the previous one away, ONCE. After that the shopper
    // owns the accordion: reopening Step 1 later must stick, so this never fires
    // twice for the same step and never forces a section shut.
    var COLLAPSE_CHAIN = { s2: 's1', s3: 's2', adv: 'std', ips: 's3' };
    var autoCollapsed = {};
    function collapsible(id, title, node, defaultOpen, meta) {
      meta = meta || {};
      var d = document.createElement('details');
      d.className = 'v2-acc';
      d.setAttribute('data-acc', id);
      d.open = (id in openMap) ? openMap[id] : !!defaultOpen;
      d.addEventListener('toggle', function () {
        openMap[id] = d.open;
        if (!d.open || !COLLAPSE_CHAIN[id] || autoCollapsed[id]) return;
        autoCollapsed[id] = true;
        var prevId = COLLAPSE_CHAIN[id];
        openMap[prevId] = false;
        var prev = dialog.querySelector('details[data-acc="' + prevId + '"]');
        // Collapse the DOM node directly instead of re-rendering: a full
        // re-render here would drop focus out of whatever the shopper touched.
        if (prev) prev.open = false;
      });
      var s = document.createElement('summary');
      var bodyPad = el('div', meta.flushBody ? 'padding:0 0 14px;' : 'padding:0 14px 14px;');
      var wrap = el('div', '', '');
      wrap.className = 'v2-sum-wrap';
      var titleRow = el('div', 'display:flex;align-items:center;', '');
      if (meta.selected) titleRow.appendChild(el('span', '', '<span class="v2-check">✓</span>'));
      titleRow.appendChild(el('span', '', title));
      if (meta.badge) titleRow.appendChild(el('span', '', '<span class="v2-pill">' + meta.badge + '</span>'));
      if (meta.info) titleRow.appendChild(el('span', '', '<span class="v2-info">' + meta.info + '</span>'));
      wrap.appendChild(titleRow);
      if (meta.selected && meta.sub) {
        var sub = el('div', '', meta.sub);
        sub.className = 'v2-sub';
        wrap.appendChild(sub);
      }
      s.appendChild(wrap);
      d.appendChild(s);
      bodyPad.appendChild(node);
      d.appendChild(bodyPad);
      return d;
    }
    // Step 1 summarises EVERY dropdown the product has, changed or not: leaving
    // the standard option is itself a decision, so the tick shows as soon as the
    // product has any dropdown at all. Products with none get no tick and no
    // summary — only the no_options_sentence inside.
    // (Steps 2 and 3 keep the opposite rule: tick only on a real selection.)
    function metaS1() {
      var defs = choicesFor(productTags);
      if (defs.length === 0) return { selected: false, sub: '', badge: 'Free' };
      var parts = defs.map(function (c) {
        // Unchanged reads as whatever the select itself shows, so the summary
        // never claims a wording the shopper cannot see in the dropdown.
        return c.label + ': ' + (state.choices[c.label] || c.placeholder || CHOICE_PLACEHOLDER);
      });
      return { selected: true, sub: parts.join(' · '), badge: 'Free' };
    }
    function metaS2() {
      if (!state.step2) return { selected: false, sub: '', badge: 'Free' };
      var it = itemsByKey()[state.step2];
      var txt = state.step2Text.trim();
      return {
        selected: true,
        sub: shortLabel(it) + (txt ? ' — ' + (txt.length > 48 ? txt.slice(0, 48) + '…' : txt) : ''),
        badge: 'Free'
      };
    }
    // "2 customizations · €18,00" rather than "2 added": the collapsed row has
    // to say WHAT was added, and it is the only place a shopper sees the tier's
    // subtotal once the section is shut.
    function countLabel(n) { return n + (n === 1 ? ' customization' : ' customizations'); }
    function metaS3() {
      var n = requestCount();
      // "from €9,00" is an entry price — it only makes sense before anything is
      // chosen. Once there are blocks the header owes the real total instead.
      if (n === 0) return { selected: false, sub: '', info: 'from ' + fmt(tierPrice('standard')) };
      return { selected: true, sub: countLabel(n) + ' · ' + fmt(total()) };
    }
    function metaTier(listName) {
      var tier = listName === 'step3_standard' ? 'standard' : 'advanced';
      var n = countedRequests(tier).length;
      return { selected: n > 0, sub: n > 0 ? countLabel(n) + ' · ' + fmt(n * tierPrice(tier)) : '' };
    }

    // ---------- render ----------
    var wizardIndex = 0;
    var uidSeq = 0;
    function uid(prefix) { uidSeq++; return 'v2-' + prefix + '-' + uidSeq; }
    // Every Save / Add / Remove / Edit re-renders the body. Keep keyboard focus on
    // the control with the same data-focus-key, falling back to the matching
    // Edit / Save of that block, so focus is not dropped to the top (row 1187).
    var FOCUS_FALLBACK = { save: ['edit', 'rm'], edit: ['ta', 'save'], ta: ['edit'], rm: [] };
    function focusKeyNow() {
      var a = document.activeElement;
      return a && dialog.contains(a) ? a.getAttribute('data-focus-key') : null;
    }
    function restoreFocus(key) {
      if (!key) return;
      var body = dialog.querySelector('[data-v2-body]');
      var find = function (k) {
        var hit = null;
        Array.prototype.some.call(body.querySelectorAll('[data-focus-key]'), function (n) {
          if (n.getAttribute('data-focus-key') === k) { hit = n; return true; }
          return false;
        });
        return hit;
      };
      var target = find(key);
      var parts = key.split(':');
      if (!target && FOCUS_FALLBACK[parts[parts.length - 1]]) {
        FOCUS_FALLBACK[parts[parts.length - 1]].some(function (suffix) {
          target = find(parts.slice(0, -1).concat(suffix).join(':'));
          return !!target;
        });
      }
      if (target) target.focus({ preventScroll: true });
    }
    function render() {
      var focusKey = focusKeyNow();
      var body = dialog.querySelector('[data-v2-body]');
      body.innerHTML = '';
      var s4 = buildStep4();
      if (LAYOUT === 'v1') {
        body.appendChild(collapsible('s1', CFG.ui.step1.heading, buildStep1(false), true, metaS1()));
        body.appendChild(collapsible('s2', CFG.ui.step2.heading, buildStep2(false), false, metaS2()));
        body.appendChild(collapsible('s3', CFG.ui.step3.heading, buildStep3(true, false), false, metaS3()));
        body.appendChild(s4);
      } else if (LAYOUT === 'v2') {
        var steps = [buildStep1(), buildStep2(), buildStep3(false), s4];
        steps.forEach(function (s, i) { s.style.display = i === wizardIndex ? '' : 'none'; body.appendChild(s); });
        var nav = el('div', 'display:flex;gap:8px;margin-top:10px;');
        if (wizardIndex > 0) {
          var back = el('button', 'flex:1;padding:10px;border:1px solid #ddd;background:#fff;border-radius:4px;cursor:pointer;', 'Back');
          back.type = 'button'; back.onclick = function () { wizardIndex--; render(); };
          nav.appendChild(back);
        }
        if (wizardIndex < steps.length - 1) {
          var next = el('button', 'flex:1;padding:10px;border:none;background:#000;color:#fff;border-radius:4px;cursor:pointer;', 'Next');
          next.type = 'button'; next.onclick = function () { wizardIndex++; render(); };
          nav.appendChild(next);
        }
        body.appendChild(nav);
      } else {
        body.appendChild(buildStep1());
        body.appendChild(buildStep2());
        body.appendChild(buildStep3(true));
        body.appendChild(s4);
      }
      refreshFooter();
      restoreFocus(focusKey);
    }
    function refresh() { render(); }
    function refreshFooter() {
      var warn = dialog.querySelector('[data-v2-warn]');
      var totalRow = dialog.querySelector('[data-v2-total]');
      warn.style.display = anySelection() ? 'block' : 'none';
      totalRow.innerHTML =
        '<span style="font-weight:500;color:#000;">Customizations Total:</span>' +
        '<span style="font-weight:600;font-size:1.1rem;color:#000;">' + fmt(total()) + '</span>';
    }
    function updateContextLine() {
      var c = dialog.querySelector('[data-v2-context]');
      if (!c) return;
      var title = ctx.productTitle ||
        (document.querySelector('h1') ? document.querySelector('h1').textContent : document.title);
      var v = selectedVariant();
      var second = v && Array.isArray(v.options) ? v.options.join(' · ') : (v ? v.title : '');
      c.innerHTML = '';
      c.appendChild(el('div', '', String(title).trim()));
      if (second) c.appendChild(el('div', '', second));
    }

    // ---------- properties ----------
    // NAME = "<item label> (<price or free>)", VALUE = the shopper's text (or
    // the dropdown selection). The trailing marker is what the cart renderer
    // keys on. Prices are ALWAYS recomputed from today's tariffs.
    function buildProperties() {
      var out = [];
      var map = itemsByKey();
      var breakdown = [];
      Object.keys(state.choices).forEach(function (label) {
        if (state.choices[label]) out.push([label + ' (free)', state.choices[label]]);
      });
      // Saved only, exactly like the Step-3 requests below.
      if (state.step2 && state.step2Saved) out.push([shortLabel(map[state.step2]) + ' (free)', state.step2Text.trim()]);
      // Step-3 requests carry no item identity, so property names are NUMBERED:
      // Shopify line properties are a map and duplicate names would collide.
      // Standard first, then advanced, matching the on-screen order.
      var n = 0;
      ['standard', 'advanced'].forEach(function (tier) {
        var key = CFG.request_keys[tier];
        var price = tierPrice(tier);
        // Saved only — the same filter the totals use, so what the shopper was
        // quoted is exactly what the line carries.
        savedRequests(tier).forEach(function (entry) {
          n++;
          out.push(['Customization ' + n + ' (' + price + ' ' + currency + ')', entry.text.trim()]);
          breakdown.push({ item: key, amount: price });
        });
      });
      // Visible total, the counterpart of the live modal's "Customization fee".
      // The confirmation email, the admin order card and the packing slip only
      // render properties WITHOUT an underscore prefix, so _fee_total alone
      // never reaches the customer or the workshop. The cart snippet keeps its
      // own computed fee line and filters every name containing "Customization"
      // out of the generic property list, so this does not show up twice.
      if (breakdown.length && total() > 0) {
        out.push(['Customizations fee', total().toFixed(2).replace('.', ',') + ' ' + currency]);
        out.push(['_fee_breakdown', JSON.stringify(breakdown)]);
        out.push(['_fee_total', total().toFixed(2) + ' ' + currency]);
      }
      return out;
    }

    function onSave() {
      if (syncVariantRule()) return;
      if (invalidKeys().length > 0) { state.triedSave = true; render(); return; }
      if (ctx.mode === 'cart') return saveToCart();
      if (!writePdpProperties()) { notice('Could not save — please reload the page and try again.'); return; }
      hideConfirm();
      dialog.close();
      updatePdpSurface();
    }

    // PDP: hidden inputs on the product form, submitted with Add to Cart. This is
    // the COMMITTED state: what the cart will get. `committed` mirrors it so a
    // close with unsaved changes can be detected and undone (row 1187).
    function writePdpProperties() {
      var form = document.querySelector('form[data-product-form]') ||
        (document.querySelector('product-form') && document.querySelector('product-form').querySelector('form'));
      if (!form) { console.error('[modal-v2] product form not found'); return false; }
      Array.prototype.forEach.call(form.querySelectorAll('.v2-prop'), function (n) { n.remove(); });
      buildProperties().forEach(function (pair) {
        var i = document.createElement('input');
        i.type = 'hidden'; i.name = 'properties[' + pair[0] + ']'; i.value = pair[1]; i.className = 'v2-prop';
        form.appendChild(i);
      });
      committed = snapshot();
      return true;
    }

    // ---------- unsaved changes ----------
    // Vidas 2026-10-26 (row 1187): closing with unsaved changes asks first —
    // "Save changes" or "Discard changes". Before this, X kept the edited state on
    // screen while the product form still carried the old one, so a removed €9
    // request was still charged.
    function snapshot() {
      return JSON.stringify({ choices: state.choices, step2: state.step2, step2Text: state.step2Text,
        step2Saved: state.step2Saved, requests: state.requests });
    }
    function restore(snap) {
      var s = JSON.parse(snap);
      state.choices = s.choices; state.step2 = s.step2; state.step2Text = s.step2Text;
      state.step2Saved = s.step2Saved; state.requests = s.requests; state.triedSave = false;
    }
    var committed = snapshot();
    function isDirty() { return snapshot() !== committed; }
    function showConfirm() {
      var c = dialog.querySelector('[data-v2-confirm]');
      c.style.display = 'flex';
      c.querySelector('[data-v2-confirm-save]').focus();
    }
    function hideConfirm() {
      var c = dialog && dialog.querySelector('[data-v2-confirm]');
      if (c) c.style.display = 'none';
    }
    function confirmOpen() {
      var c = dialog.querySelector('[data-v2-confirm]');
      return !!c && c.style.display !== 'none';
    }
    function requestClose() {
      if (!isDirty()) { hideConfirm(); dialog.close(); return; }
      showConfirm();
    }
    function discardAndClose() {
      restore(committed);
      hideConfirm();
      dialog.close();
    }

    /**
     * PDP surface after saving — trigger label and the status block under it.
     * Both are lifted from the live modal verbatim (same element, same wording,
     * same arithmetic) because shoppers already know this pattern; the status
     * div carries its own inline styling from the section markup, so filling it
     * the way the live modal does reproduces the look exactly.
     */
    function updatePdpSurface() {
      if (!ctx.host) return;
      var triggerText = ctx.host.querySelector('[data-customizations-trigger] [data-trigger-text]');
      var triggerCount = ctx.host.querySelector('[data-trigger-count]');
      var status = ctx.host.querySelector('[data-status-message]');

      if (!anySelection()) {
        if (triggerText) triggerText.textContent = 'Need Customization?';
        if (triggerCount) triggerCount.classList.add('hidden');
        if (status) status.style.display = 'none';
        return;
      }

      if (triggerText) triggerText.textContent = 'Edit Customizations (' + fmt(total()) + ')';
      if (triggerCount) triggerCount.classList.add('hidden');
      if (!status) return;

      var v = selectedVariant();
      var itemCents = v && typeof v.price === 'number' ? v.price
        : parseInt((ctx.host.dataset && ctx.host.dataset.productPriceCents) || '0', 10);
      var custCents = Math.round(total() * 100);
      var price = function (cents) { return (cents / 100).toFixed(2) + ' ' + currency; };
      status.innerHTML =
        '<strong>✓ Customizations applied!</strong><br>' +
        '<span style="font-size: 0.9em; opacity: 0.9;">' +
        'Item: ' + price(itemCents) + ' + Customization: ' + price(custCents) +
        ' = <strong>' + price(itemCents + custCents) + '</strong>' +
        '</span>';
      status.style.display = 'block';
    }

    function saveToCart() {
      // Properties the modal does not own (anything it could not parse, plus any
      // other app's) are carried over unchanged; only its own are rebuilt.
      var props = {};
      var oldProps = (ctx.initial && ctx.initial.properties) || {};
      var consumed = (ctx.initial && ctx.initial.consumed) || [];
      Object.keys(oldProps).forEach(function (name) {
        if (consumed.indexOf(name) === -1) props[name] = oldProps[name];
      });
      buildProperties().forEach(function (pair) { props[pair[0]] = pair[1]; });
      hideConfirm();
      // Nothing changed: the line already carries exactly this. (Adding an
      // identical line would merge into the old one, and removing the old key
      // afterwards would then take both away.)
      if (JSON.stringify(sortedKeys(props)) === JSON.stringify(sortedKeys(oldProps))) {
        committed = snapshot();
        dialog.close();
        return;
      }
      var save = dialog.querySelector('[data-v2-save]');
      if (save) { save.disabled = true; save.textContent = 'Updating...'; }
      var post = function (url, body) {
        return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
          .then(function (r) { if (!r.ok) throw new Error(url + ' ' + r.status); return r.json(); });
      };
      // Row 1187: add the new line FIRST and take the old one out only after the
      // add succeeded. The old order (out, then add) lost the shopper's item
      // whenever the add failed — sold out, a network drop, a 422.
      var added = false;
      post('/cart/add.js', { id: ctx.variantId, quantity: ctx.quantity || 1, properties: props })
        .then(function () { added = true; return post('/cart/change.js', { id: ctx.cartItemKey, quantity: 0 }); })
        .then(function () { window.location.reload(); })
        .catch(function (err) {
          console.error('[modal-v2] cart update failed', err);
          if (added) {
            // The new line is in; only the old one could not be taken out.
            // Nothing is lost, but the shopper must see both lines and why.
            notice('Your updated item was added, but the previous one could not be removed — please remove it from the cart.');
            setTimeout(function () { window.location.reload(); }, 4000);
            return;
          }
          if (save) { save.disabled = false; save.textContent = 'Save'; }
          notice('Could not update the cart — please try again. Your item is still in the cart.');
        });
    }
    function sortedKeys(o) {
      var out = {};
      Object.keys(o).sort().forEach(function (k) { out[k] = o[k]; });
      return out;
    }

    // ---------- dialog shell ----------
    var dialog = document.createElement('dialog');
    dialog.className = 'product-modal customizations-modal v2-dialog';
    var titleId = 'v2-title-' + Math.random().toString(36).slice(2, 8);
    dialog.setAttribute('aria-labelledby', titleId);
    dialog.innerHTML =
      '<style>' +
      '.v2-dialog{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);padding:0;border:none;' +
      'border-radius:8px;box-shadow:0 4px 20px rgba(0,0,0,.2);max-width:500px;width:90%;max-height:90vh;' +
      'flex-direction:column;z-index:10000;background:#fff;overflow:hidden;font-family:inherit;}' +
      '.v2-dialog[open]{display:flex;}' +
      '.v2-scroll{padding:0 32px 32px;overflow-y:auto;overflow-x:hidden;-webkit-overflow-scrolling:touch;' +
      'overscroll-behavior:contain;flex:1 1 auto;min-height:0;}' +
      '.v2-head{position:sticky;top:0;background:#fff;z-index:3;padding:32px 0 12px;margin-bottom:4px;}' +
      '.v2-foot{flex-shrink:0;background:#fff;border-top:1px solid var(--COLOR-BORDER,#e0e0e0);' +
      'box-shadow:0 -4px 12px rgba(0,0,0,.06);padding:12px 32px 16px;}' +
      '@media (max-width:749px){.v2-dialog{top:0;left:0;transform:none;width:100vw;max-width:none;' +
      'height:100dvh;max-height:none;border-radius:0;}}' +
      '.v2-acc{border:1px solid var(--COLOR-BORDER,#e0e0e0);border-radius:4px;margin:0 -12px 16px;background:#FBFAF5;}' +
      '.v2-acc .v2-acc{margin-left:0;margin-right:0;}' +
      '.v2-acc[open]{background:white;}' +
      '.v2-acc>summary{list-style:none;display:flex;align-items:center;justify-content:space-between;' +
      'cursor:pointer;padding:12px 14px;font-weight:500;font-size:1rem;color:#000;}' +
      '.v2-acc>summary::-webkit-details-marker{display:none;}' +
      '.v2-acc>summary:hover{background:rgba(0,0,0,0.05);border-radius:4px;}' +
      '.v2-acc>summary::after{content:"";width:8px;height:8px;border-right:1.5px solid #000;' +
      'border-bottom:1.5px solid #000;transform:rotate(45deg);transition:transform .15s ease;margin-left:12px;flex-shrink:0;}' +
      '.v2-acc[open]>summary::after{transform:rotate(225deg);}' +
      '.v2-sum-wrap{flex:1;display:flex;flex-direction:column;gap:2px;min-width:0;}' +
      '.v2-sub{font-size:0.75rem;color:#666;font-weight:400;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}' +
      // Child-scoped: tier accordions nest inside Step 3, so a descendant
      // selector would hide a collapsed tier's summary whenever Step 3 is open.
      '.v2-acc[open]>summary .v2-sub{display:none;}' +
      '.v2-check{color:#059669;margin-right:6px;font-weight:600;}' +
      '.v2-pill{font-size:0.75rem;font-weight:600;color:#059669;background:rgba(5,150,105,0.08);' +
      'padding:0.125rem 0.5rem;border-radius:12px;margin-left:8px;white-space:nowrap;line-height:1.5;}' +
      // Price hint matches the live modal's "Price: €9" treatment: 0.8rem / 500.
      '.v2-info{font-size:0.8rem;color:#666;margin-left:8px;white-space:nowrap;font-weight:500;}' +
      // The theme paints a black ring on :focus, which lands on the summary after
      // a mouse click and reads as an error state. Keyboard focus keeps a ring,
      // in gold, so the section is still traversable without a mouse.
      '.v2-acc>summary:focus{outline:none;}' +
      '.v2-acc>summary:focus-visible{outline:2px solid rgba(169,152,67,0.85);outline-offset:-2px;border-radius:4px;}' +
      // IPS card: the heading bar carries the gold, the body stays cream like the
      // numbered steps.
      //
      // White type on #A99843 measures 2.9:1, under both the 4.5:1 normal-text
      // and the 3:1 large-text threshold; black measured 7.3:1. Vidas chose white
      // on 2026-08-10 after seeing the numbers and a screenshot of each option —
      // a deliberate call, not an oversight. Do not "fix" it back to black; if
      // the contrast is ever revisited, the fix that keeps white legible is a
      // darker gold (#7A6C2B measures 5.2:1), not a type-colour change.
      //
      // The card border is removed with it: a white border reads as nothing
      // against the cream page, so the bar is the card's only edge.
      // These rules follow the generic focus block on purpose — same specificity,
      // so the later one wins and the gold ring can be swapped for a black one.
      '.v2-acc--ips{border:none;}' +
      '.v2-acc--ips[open]{background:#FBFAF5;}' +
      '.v2-acc--ips>summary{background:#A99843;color:#fff;border-radius:3px;}' +
      // The chevron inherits the generic black borders; it has to follow the type.
      '.v2-acc--ips>summary::after{border-right-color:#fff;border-bottom-color:#fff;}' +
      '.v2-acc--ips[open]>summary{border-radius:3px 3px 0 0;}' +
      // The shared hover paints translucent black, which would wash the gold out
      // entirely; the IPS bar darkens its own gold instead.
      '.v2-acc--ips>summary:hover{background:#9C8C3D;}' +
      // A gold focus ring is invisible on a gold bar.
      '.v2-acc--ips>summary:focus-visible{outline:2px solid #000;}' +
      // The theme styles <strong> as weight 500, which against the 400 body copy
      // at 0.85rem grey reads as "not bold at all". Config text uses <strong>
      // to make one word carry a rule ("select ONE"), so it has to be legible.
      '.v2-dialog p strong{font-weight:700;}' +
      // Edit / Remove keep their link look but get a 44px-tall hit area; the
      // negative margin keeps the row height unchanged, so only the tap target
      // grows. Measured 67x19 before — under the 44px guideline on phones.
      '.v2-linkbtn{background:none;border:none;color:#666;cursor:pointer;font-size:0.8rem;' +
      'text-decoration:underline;padding:13px 10px;margin:-13px 0;min-width:44px;line-height:18px;}' +
      '.v2-ta-label{font-size:0.75rem;color:#666;margin:8px 0 2px;display:block;}' +
      '.v2-ta-err{border:1px solid #c0392b !important;}' +
      // Unsaved-changes prompt (row 1187): covers the modal, asks Save / Discard.
      '.v2-confirm{position:absolute;inset:0;z-index:20;background:rgba(255,255,255,.92);display:flex;' +
      'align-items:center;justify-content:center;padding:24px;}' +
      '.v2-confirm-box{background:#fff;border:1px solid var(--COLOR-BORDER,#e0e0e0);border-radius:8px;' +
      'box-shadow:0 4px 20px rgba(0,0,0,.12);padding:24px;max-width:360px;width:100%;text-align:center;}' +
      '.v2-confirm-box p{margin:0 0 18px;font-size:1rem;color:#000;line-height:1.5;}' +
      '.v2-confirm-box button{display:block;width:100%;padding:12px;border-radius:4px;font-size:1rem;cursor:pointer;margin-top:10px;}' +
      '</style>' +
      '<div class="v2-scroll" data-scroll-lock-scrollable>' +
      '<div class="v2-head" style="position:sticky;">' +
      '<h3 id="' + titleId + '" style="margin:0 0 4px;font-size:1.5rem;font-weight:500;color:#000;text-align:center;">Customize Your Item</h3>' +
      '<div data-v2-context style="font-size:0.8rem;color:#999;text-align:center;line-height:1.4;margin-bottom:12px;"></div>' +
      '<div data-v2-notice style="display:none;padding:8px 12px;background:#FBFAF5;color:#666;border-radius:4px;font-size:0.85rem;text-align:center;margin-bottom:8px;"></div>' +
      '<button type="button" data-v2-close aria-label="Close" style="position:absolute;top:24px;right:0;border:none;background:none;font-size:28px;color:#999;line-height:1;cursor:pointer;">&times;</button>' +
      '</div>' +
      '<div data-v2-body></div>' +
      '</div>' +
      '<div class="v2-foot">' +
      '<div data-v2-total style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;"></div>' +
      '<div style="display:flex;gap:12px;">' +
      '<button type="button" data-v2-close2 style="flex:1;padding:14px;border:1px solid #ddd;background:white;border-radius:4px;cursor:pointer;font-size:1rem;">Close</button>' +
      '<button type="button" data-v2-save style="flex:2;padding:14px;border:none;background:#000;color:white;border-radius:4px;cursor:pointer;font-size:1rem;font-weight:500;">Save</button>' +
      '</div>' +
      '<p data-v2-warn style="margin:12px 0 0;font-size:0.85rem;color:#666;line-height:1.5;text-align:center;display:none;">' + CFG.ui.returns_warning + '</p>' +
      '</div>' +
      '<div class="v2-confirm" data-v2-confirm role="alertdialog" aria-label="Unsaved changes" style="display:none;">' +
      '<div class="v2-confirm-box">' +
      '<p>You have unsaved changes to your customizations.</p>' +
      '<button type="button" data-v2-confirm-save style="border:none;background:#000;color:#fff;font-weight:500;">Save changes</button>' +
      '<button type="button" data-v2-confirm-discard style="border:1px solid #ddd;background:#fff;color:#000;">Discard changes</button>' +
      '</div>' +
      '</div>';
    document.body.appendChild(dialog);
    dialog.querySelector('[data-v2-close]').addEventListener('click', requestClose);
    dialog.querySelector('[data-v2-close2]').addEventListener('click', requestClose);
    dialog.querySelector('[data-v2-save]').addEventListener('click', onSave);
    dialog.querySelector('[data-v2-confirm-save]').addEventListener('click', function () { hideConfirm(); onSave(); });
    dialog.querySelector('[data-v2-confirm-discard]').addEventListener('click', discardAndClose);
    // Escape: with unsaved changes ask first; Escape on the prompt discards.
    // (Chrome force-closes on a second Escape anyway, so this is the only
    // behaviour every browser can keep.)
    dialog.addEventListener('cancel', function (e) {
      e.preventDefault();
      if (confirmOpen()) { discardAndClose(); return; }
      requestClose();
    });
    dialog.addEventListener('close', function () {
      hideConfirm();
      // A close that bypassed requestClose (the browser force-closes on a repeated
      // Escape) must not leave unsaved edits on screen that the form does not carry.
      if (isDirty()) restore(committed);
      if (ctx.mode === 'cart') { dialog.remove(); }
    });

    var pollTimer = null;
    function open() {
      wizardIndex = 0;
      var n = dialog.querySelector('[data-v2-notice]');
      if (n) n.style.display = 'none';
      lastBlocked = null;
      syncVariantRule();
      render();
      updateContextLine();
      hideConfirm();
      dialog.showModal();
      // Cart mode: the variant is fixed on the line — nothing to poll. PDP polls
      // from mount on, also while the modal is closed (row 1187, syncVariantRule).
      // Reopened cart line: today's tariffs may total differently than stored.
      if (ctx.mode === 'cart' && ctx.initial) {
        var storedParts = String(ctx.initial.storedTotal || '').split(' ');
        var storedAmount = storedParts.length === 2 ? Number(storedParts[0]) : null;
        var storedCur = storedParts.length === 2 ? storedParts[1] : null;
        if (storedAmount !== null && storedCur === currency && Math.abs(storedAmount - total()) > 0.001) {
          notice(CFG.ui.prices_updated_msg);
        }
        if (ctx.initial.notes && ctx.initial.notes.length) {
          console.warn('[modal-v2] cart line items not restored:', ctx.initial.notes);
        }
      }
    }

    if (ctx.mode === 'pdp') pollTimer = setInterval(syncVariantRule, 600);
    if (ctx.mode === 'pdp' && currentHandle) {
      fetch('/products/' + currentHandle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (pj) { ingestProduct(pj); lastBlocked = null; syncVariantRule(); })
        .catch(function () { console.warn('[modal-v2] product.js fetch failed — price rule inactive'); });
    } else if (ctx.productJson) {
      ingestProduct(ctx.productJson);
    }

    return { open: open, dialog: dialog };
  }

  // ---------- entry point 1: PDP ----------
  var pdpBooted = false;
  function initPdp() {
    if (pdpBooted) return;
    var host = document.querySelector('product-customizations');
    if (!host) return;
    pdpBooted = true;
    var ds = host.dataset;
    var productTags = (ds.productTags || '').split(',').map(function (t) { return t.trim(); });
    var handleMatch = /\/products\/([^/?#]+)/.exec(window.location.pathname);
    var modal = mountModal({
      mode: 'pdp',
      host: host,
      productTags: productTags,
      currency: ds.currencyCode || 'USD',
      moneyFormat: ds.moneyFormat || '${{amount}}',
      productHandle: handleMatch ? handleMatch[1] : ''
    });
    var trigger = host.querySelector('[data-customizations-trigger]');
    if (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault(); e.stopImmediatePropagation();
        modal.open();
      }, true);
    }
    console.log('[modal-v2] PDP ready: layout=' + LAYOUT + ', category=' + (categoryTagOf(productTags) || 'cust-dresses (fallback)'));
  }
  document.addEventListener('DOMContentLoaded', initPdp);
  if (document.readyState !== 'loading') initPdp();

  // ---------- entry point 2: CART edit ----------
  // Capture phase so the live modal's bubble-phase listener never runs for v2
  // lines. Legacy lines are left alone entirely — no interception, no flags.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-edit-customizations]');
    if (!btn) return;
    if (btn.getAttribute('data-cust-format') !== 'v2') return; // legacy -> live modal
    e.preventDefault();
    e.stopImmediatePropagation();
    var cartItemKey = btn.dataset.cartItemKey;
    var productUrl = btn.dataset.productUrl;
    Promise.all([
      fetch('/cart.js').then(function (r) { return r.json(); }),
      fetch(productUrl + '.js').then(function (r) { return r.json(); })
    ]).then(function (res) {
      var cart = res[0], product = res[1];
      var item = cart.items.find(function (i) { return i.key === cartItemKey; });
      if (!item) { console.warn('[modal-v2] cart line not found', cartItemKey); return; }
      var productTags = product.tags || [];
      var category = resolveCategory(categoryTagOf(productTags) || 'cust-dresses');
      var choiceLabels = choicesFor(productTags).map(function (c) { return c.label; });
      var initial = stateFromProperties(item.properties, category, choiceLabels);
      initial.properties = item.properties || {};
      var modal = mountModal({
        mode: 'cart',
        productTags: productTags,
        currency: cart.currency,
        moneyFormat: (window.theme && window.theme.moneyFormat) || '${{amount}}',
        productHandle: product.handle,
        productTitle: product.title,
        productJson: product,
        variants: product.variants || [],
        variantId: item.variant_id,
        quantity: item.quantity || 1,
        cartItemKey: cartItemKey,
        initial: initial
      });
      modal.open();
    }).catch(function (err) { console.error('[modal-v2] cart edit load failed', err); });
  }, true);
})();
